(() => {
  const initCarousel = () => {
    const carouselContainer = document.querySelector("#product-image-zoom");
    const carouselSplide = carouselContainer.querySelector(".splide");
    const carouselSplideList = carouselSplide.querySelector(".splide__list");
    const carouselLeftButton = carouselContainer.querySelector(
      ".product-image-zoom__button-left",
    );
    const carouselRightButton = carouselContainer.querySelector(
      ".product-image-zoom__button-right",
    );
    const carouselZoomButton = carouselContainer.querySelector(
      ".product-image-zoom__button-zoom",
    );
    const carouselExitButton = carouselContainer.querySelector(
      ".product-image-zoom__button-exit",
    );
    const carouselLoader = carouselContainer.querySelector(
      ".product-image-zoom__loader",
    );
    const carouselButtons = carouselContainer.querySelector(
      ".product-image-zoom__buttons",
    );

    const state = {
      lastImageSet: null,
      splide: null,
      backdropId: null,
      openToken: 0,
      releaseFocus: null,
      zoomed: false,
      currentImages: [],
      pendingKeys: [],
    };

    const prefersReducedMotion = () =>
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const buildSplideOptions = (startIndex, zoomed) => ({
      start: startIndex,
      type: "slide",
      autoWidth: zoomed ? false : true,
      perPage: 1,
      pagination: false,
      focus: "center",
      gap: "0.625rem",
      padding: zoomed ? "0" : "0.75rem",
      arrows: false,
      trimSpace: true,
      breakpoints: {
        800: {
          autoWidth: false,
          padding: "0",
        },
      },
    });

    const carouselLeftBtnClick = () => {
      state.splide?.go("-1");
    };

    const carouselRightBtnClick = () => {
      state.splide?.go("+1");
    };

    const carouselCloseBtnClick = () => {
      if (state.backdropId !== null) {
        window.popBackdrop(state.backdropId);
      }
    };

    const stepOrZoom = (key) => {
      if (key === "ArrowLeft") state.splide.go("-1");
      if (key === "ArrowRight") state.splide.go("+1");
      if (key === "ArrowUp" && !state.zoomed) carouselZoomBtnClick();
      if (key === "ArrowDown" && state.zoomed) carouselZoomBtnClick();
    };

    const carouselArrowKeydown = (ev) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(ev.key)) return;
      ev.preventDefault();
      if (state.splide) stepOrZoom(ev.key);
      else state.pendingKeys.push(ev.key);
    };

    const carouselBackgroundClick = (ev) => {
      if (ev.target.closest(".product-image-zoom__image, .product-image-zoom__buttons")) return;
      carouselCloseBtnClick();
    };

    const toggleZoomAt = (index) => {
      if (!state.splide || window.matchMedia("(max-width: 768px)").matches) return;
      state.zoomed = !state.zoomed;

      state.splide.destroy(true);
      mountSplide(index, state.zoomed);

      carouselContainer.classList.toggle("product-image-zoom--zoomed", state.zoomed);
      carouselZoomButton.setAttribute("aria-pressed", String(state.zoomed));
      carouselZoomButton.setAttribute("aria-label", state.zoomed ? "Zoom out" : "Zoom in");
      updateCarouselBtnStates(index, state.currentImages.length);
    };

    const carouselZoomBtnClick = () => {
      toggleZoomAt(state.splide?.index);
    };

    const updateCarouselBtnStates = (destIndex, maxPages) => {
      carouselLeftButton.disabled = destIndex === 0;
      carouselRightButton.disabled = destIndex >= maxPages - 1;
    };

    const photosInReach = () => {
      const slide = carouselSplideList.firstElementChild;
      if (!slide?.offsetWidth) return 1;
      const gap = parseFloat(getComputedStyle(slide).marginRight) || 0;
      return Math.max(1, Math.ceil(carouselSplide.offsetWidth / (slide.offsetWidth + gap)));
    };

    const loadNearbyPhotos = (index) => {
      const photos = carouselContainer.querySelectorAll(
        ".product-image-zoom__image",
      );
      const reach = photosInReach();
      const last = Math.min(photos.length - 1, index + reach);
      for (let i = Math.max(0, index - reach); i <= last; i++) {
        if (!photos[i].hasAttribute("src")) photos[i].src = photos[i].dataset.src;
      }
    };

    const loadPhotosNearScreen = () => {
      const screen = carouselSplide.getBoundingClientRect();
      const photos = carouselContainer.querySelectorAll(
        ".product-image-zoom__image:not([src])",
      );
      for (let i = 0; i < photos.length; i++) {
        const slide = photos[i].parentElement.getBoundingClientRect();
        if (slide.right > screen.left - slide.width && slide.left < screen.right + slide.width) {
          photos[i].src = photos[i].dataset.src;
        }
      }
    };

    const mountSplide = (index, zoomed) => {
      state.splide = new Splide(carouselSplide, buildSplideOptions(index, zoomed));
      state.splide.mount();
      loadNearbyPhotos(index);
      state.splide.on("move", (newIndex, prevIndex, destIndex) => {
        loadNearbyPhotos(destIndex);
        updateCarouselBtnStates(destIndex, state.currentImages.length);
      });
      state.splide.on("dragging resized", loadPhotosNearScreen);
    };

    const criticalPhotos = (index) => {
      const photos = carouselContainer.querySelectorAll(
        ".product-image-zoom__image",
      );
      return [photos[index - 1], photos[index], photos[index + 1]].filter(Boolean);
    };

    const photosReady = (images, index) =>
      state.lastImageSet === JSON.stringify(images) &&
      criticalPhotos(index).every((photo) => photo.hasAttribute("src") && photo.complete);

    const showPhotos = (images, index, token) => {
      const imageSet = JSON.stringify(images);
      if (state.lastImageSet !== imageSet) {
        state.lastImageSet = imageSet;
        for (let i = 0; i < images.length; i++) {
          const imageElem = createImageGridItem(images[i], i);
          carouselSplideList.appendChild(imageElem);
        }
      }

      setImagesFirstFrame();
      state.splide?.destroy(true);
      mountSplide(index, false);

      carouselLeftButton.addEventListener("click", carouselLeftBtnClick);
      carouselRightButton.addEventListener("click", carouselRightBtnClick);
      carouselExitButton.addEventListener("click", carouselCloseBtnClick);
      carouselZoomButton.addEventListener("click", carouselZoomBtnClick);
      carouselContainer.addEventListener("click", carouselBackgroundClick);

      waitForCriticalImagesThenAnimate(index, token);
      updateCarouselBtnStates(index, images.length);
      state.pendingKeys.splice(0).forEach(stepOrZoom);
    };

    const open = (images, index = 0) => {
      const token = ++state.openToken;
      state.currentImages = images;
      state.zoomed = false;
      state.pendingKeys = [];
      carouselContainer.classList.remove("product-image-zoom--zoomed");
      carouselZoomButton.setAttribute("aria-pressed", "false");
      carouselZoomButton.setAttribute("aria-label", "Zoom in");

      const ready = photosReady(images, index);
      carouselButtons.hidden = true;
      carouselLoader.hidden = ready;
      carouselContainer.setAttribute("aria-busy", String(!ready));
      carouselContainer.classList.add("product-image-zoom--open");

      carouselContainer.removeAttribute("inert");

      window.freezeDocumentHeight();
      state.backdropId = window.pushBackdrop({
        closeCallback: () => close(),
        hideNavbar: true,
        clickToClose: true,
      });
      state.releaseFocus = window.holdOverlayFocus(carouselContainer, carouselContainer, carouselCloseBtnClick);
      document.addEventListener("keydown", carouselArrowKeydown);

      if (ready && prefersReducedMotion()) {
        showPhotos(images, index, token);
      } else {
        window.afterNextPaint(() => {
          if (token === state.openToken) showPhotos(images, index, token);
        });
      }
    };

    const setImagesFirstFrame = () => {
      const foundImages = carouselContainer.querySelectorAll(
        ".product-image-zoom__image",
      );
      for (let i = 0; i < foundImages.length; i++) {
        foundImages[i].style.transform = "scale(0.85)";
        foundImages[i].style.opacity = 0;
      }
    };

    const waitForImageLoad = (img) =>
      new Promise((resolve) => {
        if (img.complete) {
          resolve();
          return;
        }
        const onSettle = () => {
          img.removeEventListener("load", onSettle);
          img.removeEventListener("error", onSettle);
          resolve();
        };
        img.addEventListener("load", onSettle);
        img.addEventListener("error", onSettle);
      });

    const waitForCriticalImagesThenAnimate = async (index, token) => {
      await Promise.all(criticalPhotos(index).map(waitForImageLoad));

      if (token !== state.openToken) return;

      carouselLoader.hidden = true;
      carouselButtons.hidden = false;
      carouselContainer.setAttribute("aria-busy", "false");
      playStaggerAnimations(index);
    };

    const playStaggerAnimations = (currentIndex) => {
      const staggerTime = 150;
      const reducedMotion = prefersReducedMotion();

      const foundImages = carouselContainer.querySelectorAll(
        ".product-image-zoom__image",
      );
      for (let i = 0; i < foundImages.length; i++) {
        const delayIndex = Math.abs(i - currentIndex);
        const imageDelayTime = staggerTime * delayIndex;

        const foundImage = foundImages[i];
        foundImage.style.transform = "scale(0.85)";
        foundImage.style.opacity = 0;

        if (reducedMotion) {
          foundImage.style.transform = "scale(1)";
          foundImage.style.opacity = 1;
          continue;
        }

        const imageAnimation = [
          { transform: "scale(0.85)", opacity: "0" },
          { transform: "scale(1)", opacity: "1" },
        ];

        const imageAnimationTiming = {
          duration: 300,
          iterations: 1,
          easing: "ease",
        };

        setTimeout(() => {
          foundImage.style.transform = "scale(1)";
          foundImage.style.opacity = 1;
          foundImage.animate(imageAnimation, imageAnimationTiming);
        }, imageDelayTime);
      }
    };

    const close = () => {
      state.openToken++;
      state.zoomed = false;

      carouselContainer.classList.remove("product-image-zoom--open");
      carouselContainer.classList.remove("product-image-zoom--zoomed");
      carouselContainer.setAttribute("inert", "true");
      carouselLeftButton.removeEventListener("click", carouselLeftBtnClick);
      carouselRightButton.removeEventListener("click", carouselRightBtnClick);
      carouselExitButton.removeEventListener("click", carouselCloseBtnClick);
      carouselZoomButton.removeEventListener("click", carouselZoomBtnClick);
      carouselContainer.removeEventListener("click", carouselBackgroundClick);
      document.removeEventListener("keydown", carouselArrowKeydown);

      carouselLoader.hidden = true;
      carouselButtons.hidden = true;
      carouselContainer.setAttribute("aria-busy", "false");
      window.unfreezeDocumentHeight();

      state.splide?.destroy(true);
      state.splide = null;
      carouselSplide.classList.remove("is-initialized");
      state.releaseFocus?.();
      state.releaseFocus = null;
    };

    const createImageGridItem = ({ src, alt }, index) => {
      const slide = document.createElement("div");
      slide.classList.add("splide__slide");
      slide.classList.add("product-image-zoom__image-slide");

      const img = document.createElement("img");
      img.classList.add("product-image-zoom__image");
      img.dataset.src = src;
      img.alt = alt;
      img.addEventListener("click", () => toggleZoomAt(index));

      slide.appendChild(img);
      return slide;
    };

    return {
      open,
      close,
      zoom: carouselZoomBtnClick,
    };
  };

  const initProductImageCarouselLink = (carousel) => {
    const images = document.querySelectorAll(".product-images-container img");
    const photos = new Array(images.length);
    for (let i = 0; i < images.length; i++) {
      const imgElem = images[i];
      photos[i] = {
        src: imgElem.src,
        alt: imgElem.closest("video")?.getAttribute("aria-label") ?? imgElem.alt,
      };
      const opener = imgElem.closest("button") || imgElem;
      opener.addEventListener("click", () => {
        carousel.open(photos, i);
      });
    }
  };

  window.initScrollSnapCarousel(
    ".product-images-container",
    ".product-images-carousel__button--prev",
    ".product-images-carousel__button--next",
  );
  const carousel = initCarousel();
  initProductImageCarouselLink(carousel);
})();
